import { ApiError } from '../api/client';
import { APP_VERSION } from './appVersion';

export type PurchasePhase = 'prepare' | 'order' | 'approved' | 'verify' | 'finish';
export type PurchaseFailureReason = 'user_cancelled' | 'payment_declined' | 'store_unavailable'
  | 'already_owned' | 'owner_conflict' | 'verify_failed' | 'verify_pending'
  | 'offer_missing' | 'replace_token_missing' | 'network' | 'unknown';
export type IapErrorCode = 'unavailable' | 'cancelled' | 'verify_failed' | 'store_error';
export interface IapErrorDetails {
  detailCode?: string; phase?: PurchasePhase; storeCode?: number | string; billingCode?: number;
  httpStatus?: number; errorCode?: string; requestId?: string; retryable?: boolean;
}
export class IapError extends Error implements IapErrorDetails {
  declare detailCode?: string;
  declare phase?: PurchasePhase;
  declare storeCode?: number | string;
  declare billingCode?: number;
  declare httpStatus?: number;
  declare errorCode?: string;
  declare requestId?: string;
  declare retryable?: boolean;
  constructor(public code: IapErrorCode, msg?: string, details: IapErrorDetails = {}) {
    super(msg ?? code);
    this.name = 'IapError';
    Object.assign(this, { detailCode: msg, ...details });
  }
}
function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}
export function fromStoreError(error: unknown, phase: PurchasePhase = 'order'): IapError {
  if (error instanceof IapError) return error;
  const raw = record(error);
  const storeCode = typeof raw.code === 'string' || typeof raw.code === 'number' ? raw.code : undefined;
  const message = typeof raw.message === 'string' ? raw.message : 'store_error';
  // Die Android-Bridge älterer Wrapper bettet den Billing-Code in die Meldung ein.
  const embedded = /(?:response(?:\s*code)?|billing(?:\s*code)?)\s*[:=]?\s*(-?\d+)/i.exec(message)
    ?? /^(-?\d+)\s*\|/.exec(message);
  const symbolicCodes: Record<string, number> = { ERROR: 6, SERVICE_TIMEOUT: -3, FEATURE_NOT_SUPPORTED: -2, SERVICE_DISCONNECTED: -1,
    USER_CANCELED: 1, SERVICE_UNAVAILABLE: 2, BILLING_UNAVAILABLE: 3, ITEM_UNAVAILABLE: 4, DEVELOPER_ERROR: 5,
    ITEM_ALREADY_OWNED: 7, ITEM_NOT_OWNED: 8, NETWORK_ERROR: 12 };
  const symbolic = message.trim() === 'ERROR' ? 'ERROR' : Object.keys(symbolicCodes).filter(code => code !== 'ERROR').find(code => new RegExp(`\\b${code}\\b`).test(`${message} ${storeCode ?? ''}`));
  const embeddedCode = embedded && Number(embedded[1]) >= -3 && Number(embedded[1]) <= 12 ? Number(embedded[1]) : undefined;
  const billing = raw.billingCode ?? raw.billing_code ?? raw.responseCode ?? (symbolic ? symbolicCodes[symbolic] : embeddedCode);
  const billingCode = typeof billing === 'number' ? billing : undefined;
  const cancelled = Number(storeCode) === 6777006 || /^(PAYMENT_CANCELLED|USER_CANCELED|USER_CANCELLED)$/i.test(String(storeCode)) || billingCode === 1;
  return new IapError(cancelled ? 'cancelled' : 'store_error', message, {
    phase, storeCode, billingCode,
    detailCode: typeof raw.detailCode === 'string' ? raw.detailCode : symbolic ?? (typeof storeCode === 'string' ? storeCode : undefined),
    retryable: typeof raw.retryable === 'boolean' ? raw.retryable : undefined,
  });
}
export function fromVerificationError(error: unknown): IapError {
  if (error instanceof IapError) return error;
  if (error instanceof ApiError) {
    const raw = record(error.data);
    const legacyCode = typeof raw.error === 'string' && /^[a-z][a-z0-9_]+$/.test(raw.error) ? raw.error : undefined;
    return new IapError('verify_failed', error.code ?? (typeof raw.error === 'string' ? raw.error : 'verify_failed'), {
      phase: 'verify', httpStatus: error.status, errorCode: error.code ?? legacyCode,
      detailCode: error.code ?? legacyCode ?? 'http_error',
      requestId: error.request_id, retryable: error.retryable,
    });
  }
  return new IapError('verify_failed', record(error).name === 'AbortError' ? 'request_timeout' : 'network', {
    phase: 'verify', retryable: true,
  });
}
export function classifyPurchaseFailure(error: unknown): PurchaseFailureReason {
  const e = error instanceof IapError ? error : fromVerificationError(error);
  const text = `${e.detailCode ?? ''} ${e.errorCode ?? ''} ${e.storeCode ?? ''} ${e.message}`.toLowerCase();
  if (e.code === 'cancelled') return 'user_cancelled';
  if (/subscription_owner_mismatch/.test(text)) return 'owner_conflict';
  if (e.billingCode === 7 || /item_already_owned|already[ _]owned|already purchased|bereits gekauft/.test(text)) return 'already_owned';
  if (/verification_timeout|request_timeout|verify_pending/.test(text) || e.httpStatus === 504) {
    return e.phase === 'approved' || e.phase === 'verify' ? 'verify_pending' : 'network';
  }
  if (/replace_token_missing/.test(text)) return 'replace_token_missing';
  if (e.billingCode === 4 || /offer_missing|offer_not_loaded|invalid_offer|product_not_available/.test(text) || [6777012, 6777023, 6777029, 6777030].includes(Number(e.storeCode))) return 'offer_missing';
  if (/payment_declined|payment_not_allowed|payment_invalid|payment declined|payment rejected/.test(text) || [6777007, 6777008].includes(Number(e.storeCode))) return 'payment_declined';
  if ([-3, -2, -1, 2, 3, 6].includes(e.billingCode ?? NaN) || [429, 502, 503].includes(e.httpStatus ?? 0)
    || /store_api_unavailable|store_auth_failed|service_unavailable|billing_unavailable|iap_not_initialized/.test(text)) return 'store_unavailable';
  if (e.billingCode === 12 || [6777014, 6777025].includes(Number(e.storeCode)) || /network|offline|purchase_timeout/.test(text)) return 'network';
  if (e.code === 'verify_failed' || e.detailCode || e.httpStatus || e.billingCode !== undefined || e.storeCode !== undefined) return 'verify_failed';
  return 'unknown';
}
export type PurchaseMeta = Partial<Record<'attempt_id' | 'phase' | 'reason' | 'iap_code' | 'detail_code' | 'store_code'
  | 'billing_code' | 'http_status' | 'error_code' | 'request_id' | 'bundle_version' | 'native_version' | 'native_build'
  | 'product_id' | 'base_plan_id' | 'offer_kind' | 'relation' | 'replacement_mode' | 'old_token_present' | 'elapsed_ms', string | number | boolean>>;
export interface PurchaseAttempt {
  id: string; phase: PurchasePhase; phaseStarted: number; meta: PurchaseMeta;
}
export function createAttemptId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export function createPurchaseAttempt(meta: PurchaseMeta = {}): PurchaseAttempt {
  return { id: createAttemptId(), phase: 'prepare', phaseStarted: Date.now(), meta: { bundle_version: APP_VERSION, ...meta } };
}
export function setPurchasePhase(attempt: PurchaseAttempt, phase: PurchasePhase): void {
  attempt.phase = phase;
  attempt.phaseStarted = Date.now();
}
export function purchaseDiagnostics(attempt: PurchaseAttempt, error?: unknown): PurchaseMeta {
  const e = error instanceof IapError ? error : undefined;
  const result: PurchaseMeta = { ...attempt.meta, attempt_id: attempt.id, phase: e?.phase ?? attempt.phase,
    elapsed_ms: Math.max(0, Date.now() - attempt.phaseStarted) };
  if (e) Object.assign(result, { reason: classifyPurchaseFailure(e), iap_code: e.code, detail_code: e.detailCode,
    store_code: e.storeCode, billing_code: e.billingCode, http_status: e.httpStatus,
    error_code: e.errorCode, request_id: e.requestId });
  return Object.fromEntries(Object.entries(result).filter(([, v]) => v !== undefined)
    .map(([k, v]) => [k, typeof v === 'string' ? v.slice(0, 120) : v])) as PurchaseMeta;
}
